import { createBrowserRouter, Navigate } from "react-router-dom";
import { Suspense } from "react";
import Layout from "../layouts/MainLayout";
import { ProtectedRoute } from "./ProtectedRoute";
import { ProtectedRouteV2 } from "./ProtectedRouteV2";
import { PublicRoute } from "./PublicRoute";
import RouteError from "../shared/components/ErrorBoundary/RouteError";
import {
  AccountsList,
  AccountView,
  Login,
  Dashboard,
  StaffProfile,
  StaffProfileView,
  CustomersList,
  CustomerView,
  DriversList,
  DriverView,
  BookingsList,
  BookingView,
  BookingActionsView,
  EditBooking,
  CreateBooking,
  CreateSchedule,
  EditSchedule,
  MassEditBookings,
  ScheduleView,
  ScheduleActionsView,
  SchedulesList,
  Regions,
  AuthorityView,
  ConfigManagement,
  LiveMap,
  AuditLogs,
  Notifications,
  NotificationsV2,
  Finance,
  IntercomTest,
  AuthManagement,

  LiveLogs,
  RideMatchingMonitor,
  GhostDocuments,
  DataMigration,
  FleetOverview,
  OrganizationManagement,
  TestNotifications,
  CustomerSupport,
  AuditTest,
  FixDriverAliases,
  PersonalAssistantsList,
  PersonalAssistantView,
  B2BList,
  B2BView,
} from "./lazyComponents";

export const router = createBrowserRouter([
  {
    path: "/",
    element: <Layout />,
    errorElement: <RouteError />,
    children: [
      {
        path: "/",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<div>Loading...</div>}>
              <Dashboard />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "/live-logs",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<div>Loading...</div>}>
              <LiveLogs />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "/auth-management",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<div>Loading...</div>}>
              <AuthManagement />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "/organization",
        element: (
          <ProtectedRouteV2 permissions="staff.profiles.view">
            <Suspense fallback={<div>Loading...</div>}>
              <OrganizationManagement />
            </Suspense>
          </ProtectedRouteV2>
        ),
      },
      {
        path: "/staff-management",
        element: <Navigate to="/organization?tab=staff" replace />,
      },
      {
        path: "/permission-management",
        element: <Navigate to="/organization?tab=permissions" replace />,
      },
      {
        path: "/department-management",
        element: <Navigate to="/organization?tab=departments" replace />,
      },
      {
        path: "/job-title-management",
        element: <Navigate to="/organization?tab=jobTitles" replace />,
      },


      {
        path: "/accounts",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<div>Loading...</div>}>
              <AccountsList />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "/accounts/:id",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<div>Loading...</div>}>
              <AccountView />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "/b2b",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<div>Loading...</div>}>
              <B2BList />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "/b2b/:id",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<div>Loading...</div>}>
              <B2BView />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "/customers",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<div>Loading...</div>}>
              <CustomersList />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "/customers/:id",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<div>Loading...</div>}>
              <CustomerView />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "/drivers",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<div>Loading...</div>}>
              <DriversList />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "/drivers/:id",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<div>Loading...</div>}>
              <DriverView />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "/fleet-overview",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<div>Loading...</div>}>
              <FleetOverview />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "/personal-assistants",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<div>Loading...</div>}>
              <PersonalAssistantsList />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "/personal-assistants/:id",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<div>Loading...</div>}>
              <PersonalAssistantView />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "/bookings",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<div>Loading...</div>}>
              <BookingsList />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "/bookings/:id",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<div>Loading...</div>}>
              <BookingView />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "/bookings/:id/actions",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<div>Loading...</div>}>
              <BookingActionsView />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "/bookings/:id/edit",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<div>Loading...</div>}>
              <EditBooking />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "/bookings/create",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<div>Loading...</div>}>
              <CreateBooking />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "/schedules/create",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<div>Loading...</div>}>
              <CreateSchedule />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "/schedules/:id/edit",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<div>Loading...</div>}>
              <EditSchedule />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "/schedules/mass-edit/:id",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<div>Loading...</div>}>
              <MassEditBookings />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "/schedules/:id",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<div>Loading...</div>}>
              <ScheduleView />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "/schedules/:id/actions",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<div>Loading...</div>}>
              <ScheduleActionsView />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "/schedules",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<div>Loading...</div>}>
              <SchedulesList />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "/regions",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<div>Loading...</div>}>
              <Regions />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "/local-authority/:id",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<div>Loading...</div>}>
              <AuthorityView />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "/config-management",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<div>Loading...</div>}>
              <ConfigManagement />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "/live-map",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<div>Loading...</div>}>
              <LiveMap />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "/audit-logs",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<div>Loading...</div>}>
              <AuditLogs />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "/notifications",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<div>Loading...</div>}>
              <Notifications />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "/notifications-v2",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<div>Loading...</div>}>
              <NotificationsV2 />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "/test-notifications",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<div>Loading...</div>}>
              <TestNotifications />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "/finance",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<div>Loading...</div>}>
              <Finance />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "/intercom-test",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<div>Loading...</div>}>
              <IntercomTest />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "/customer-support",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<div>Loading...</div>}>
              <CustomerSupport />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "/staff/profile",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<div>Loading...</div>}>
              <StaffProfile />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "/staff/:id",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<div>Loading...</div>}>
              <StaffProfileView />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "/dev-tools/data-migration",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<div>Loading...</div>}>
              <DataMigration />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "/dev-tools/ghost-documents",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<div>Loading...</div>}>
              <GhostDocuments />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "/system-admin/audit-test",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<div>Loading...</div>}>
              <AuditTest />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "/system-admin/fix-driver-aliases",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<div>Loading...</div>}>
              <FixDriverAliases />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "*",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<div>Loading...</div>}>
              <RouteError />
            </Suspense>
          </ProtectedRoute>
        ),
      },
    ],
  },
  {
    path: "/auth",
    element: (
      <PublicRoute>
        <Suspense fallback={<div>Loading...</div>}>
          <Login />
        </Suspense>
      </PublicRoute>
    ),
  },
]);
